import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import { NavBar } from '../../components/NavBar';
import { LifeModelInfoModal } from '../model-signup/LifeModelInfoModal';

const SEGMENTS = [
  { imgIdx: 0, txtIdx: 0 }, // hero — I want an inspired life
  { imgIdx: 1, txtIdx: 1 }, // To see myself through the eyes of others
  { imgIdx: 2, txtIdx: 2 }, // in a thousand forms (drawing)
  { imgIdx: 3, txtIdx: 3 }, // To make the world beautiful
  { imgIdx: 4, txtIdx: 4 }, // And to fill it with art
  { imgIdx: -1, txtIdx: 5 }, // Closing section (pure black)
];

const IMAGES = [
  { src: '/images/for-models/iWantAnInspiredLife.webp', position: 'center 38%' },
  { src: '/images/for-models/ToSeeMyself1.webp', position: 'center 30%' },
  { src: '/images/for-models/ToSeeMyself2.webp', position: 'center', size: 'contain', bg: '#b8b0a0' },
  { src: '/images/for-models/ToMakeTheWorldBeautiful.webp', position: 'center 25%' },
  { src: '/images/for-models/AndFillItWithArt.webp', position: 'center 40%' },
];

export function ForModelsPage() {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [showCue, setShowCue] = useState(true);
  // Initialize textY to starting position (at p=0, drift = 0.5 * 168 = 84)
  const [textY, setTextY] = useState(84);
  const [textVisible, setTextVisible] = useState(true);
  const [initialFadeIn, setInitialFadeIn] = useState(false);
  const [showLifeModelInfo, setShowLifeModelInfo] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  const targetYRef = useRef(84);
  const currentYRef = useRef(84);
  const rafRef = useRef<number>();
  const lastVisibleIdxRef = useRef(0);

  // Initial fade-in after 0.5 seconds
  useEffect(() => {
    const timer = setTimeout(() => setInitialFadeIn(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Smooth animation loop at 60fps
  useEffect(() => {
    const animate = () => {
      // Apply the easing: yPosition = yPosition + ((targetYPosition - yPosition) / 16)
      currentYRef.current = currentYRef.current + ((targetYRef.current - currentYRef.current) / 16);
      setTextY(currentYRef.current);
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const timeline = timelineRef.current;
      if (!timeline) return;

      const docH = timeline.offsetHeight - window.innerHeight;
      const p = docH > 0 ? Math.max(0, Math.min(0.9999, window.scrollY / docH)) : 0;
      const idx = Math.min(SEGMENTS.length - 1, Math.floor(p * SEGMENTS.length));

      // Calculate progress within current segment (0 to 1)
      const segmentProgress = (p * SEGMENTS.length) - idx;

      // Text is visible in the middle portion of each segment (10% to 90%)
      // This makes text fade in after photo appears, fade out before photo disappears
      // Exception: first segment (hero) text visible immediately at 0%
      const TEXT_FADE_IN = idx === 0 ? 0 : 0.10;
      const TEXT_FADE_OUT = 0.90;
      const isTextVisible = segmentProgress >= TEXT_FADE_IN && segmentProgress <= TEXT_FADE_OUT;
      setTextVisible(isTextVisible);

      // Use overall scroll progress for continuous drift (not per-segment)
      // Scroll down = text moves up (negative Y), range of ~168px total drift (30% less than 240)
      // Centered so midpoint (p=0.5) is at original position (drift=0)
      const drift = (0.5 - p) * 168;
      targetYRef.current = drift;

      setCurrentIdx(idx);
      setShowCue(p <= 0.04);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const seg = SEGMENTS[currentIdx];

  const handleGetStarted = () => {
    if (isSignedIn) {
      navigate('/models/apply/application-form');
    } else {
      navigate('/sign-in?redirect_url=/models/apply/application-form');
    }
  };

  return (
    <>
      <style>{`
        .for-models-page {
          --ease-flow: cubic-bezier(0.37, 0, 0.18, 1);
        }
        .for-models-page body { overflow-x: hidden; }

        .fm-stage {
          position: fixed; inset: 0;
          width: 100vw; height: 100vh;
          overflow: hidden; background: #000; z-index: 1;
        }
        .fm-layer {
          position: absolute; inset: 0;
          opacity: 0; will-change: opacity;
          pointer-events: none;
          transition: opacity 1.4s var(--ease-flow);
        }
        .fm-layer.active { opacity: 1; pointer-events: auto; }

        .fm-img-layer {
          background-size: cover; background-position: center; background-color: #000;
          animation: imgDrift 28s ease-in-out infinite alternate;
        }
        .fm-img-layer.drift-reverse { animation-direction: alternate-reverse; }
        .fm-img-layer.drift-soft { animation-name: imgDriftSoft; }
        @keyframes imgDrift {
          0%   { transform: scale(1.04) translate3d(0,0,0); }
          100% { transform: scale(1.10) translate3d(-1.4%, -0.8%, 0); }
        }
        @keyframes imgDriftSoft {
          0% { transform: scale(0.99); }
          100% { transform: scale(1.04); }
        }

        .fm-vignette {
          position: absolute; inset: 0;
          background:
            linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0) 28%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.65) 100%),
            linear-gradient(95deg, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0) 55%);
          pointer-events: none; z-index: 5;
          transition: opacity 1.4s var(--ease-flow);
        }

        .fm-text-layer {
          display: flex; align-items: center; justify-content: center;
          padding: 0 8vw; z-index: 8;
        }
        .fm-text-layer .line {
          font-family: var(--font-title); font-weight: 300;
          font-size: clamp(48px, 7vw, 108px); line-height: 1.04;
          color: #fff; max-width: 22ch; margin: 0;
          text-shadow: 0 4px 32px rgba(0,0,0,0.55);
        }
        .fm-text-layer .line em { font-style: italic; color: rgba(255,255,255,0.78); font-weight: 400; }

        .fm-text-layer.tl-hero { justify-content: flex-start; align-items: flex-end; padding: 0 8vw 14vh; }
        .fm-text-layer.tl-hero .line { max-width: 14ch; font-size: clamp(64px, 9vw, 140px); }
        .fm-text-layer.tl-b1 { justify-content: flex-start; align-items: center; padding: 0 8vw; }
        .fm-text-layer.tl-b1 .line { text-align: left; max-width: 18ch; }
        .fm-text-layer.tl-b2 { justify-content: flex-end; align-items: flex-end; padding: 0 8vw 14vh; }
        .fm-text-layer.tl-b2 .line { text-align: right; max-width: 14ch; }
        .fm-text-layer.tl-b3 { justify-content: center; align-items: center; }
        .fm-text-layer.tl-b3 .line { text-align: center; max-width: 18ch; }
        .fm-text-layer.tl-b4 { justify-content: flex-end; align-items: flex-end; padding: 0 8vw 14vh; }
        .fm-text-layer.tl-b4 .line { text-align: right; max-width: 16ch; }

        .fm-closing-layer {
          display: flex; align-items: center; justify-content: center;
          flex-direction: column; gap: 28px; padding: 0 8vw;
          background: #000; z-index: 9;
        }
        .fm-closing-layer .copy-line {
          font-family: var(--font-title); font-weight: 300;
          font-size: clamp(20px, 2.1vw, 30px); line-height: 1.4;
          color: #fff; max-width: 32ch; margin: 0;
          text-align: center; text-wrap: balance;
          opacity: 0; transition: opacity 1.2s var(--ease-flow);
        }
        .fm-closing-layer .buttons {
          margin-top: 24px; opacity: 0;
          transition: opacity 1.2s var(--ease-flow);
          display: flex; gap: 16px; flex-wrap: wrap; justify-content: center;
        }
        .fm-closing-layer.active .copy-line:nth-child(1) { opacity: 1; transition-delay: 0.2s; }
        .fm-closing-layer.active .copy-line:nth-child(2) { opacity: 1; transition-delay: 1.9s; }
        .fm-closing-layer.active .copy-line:nth-child(3) { opacity: 1; transition-delay: 3.6s; }
        .fm-closing-layer.active .buttons { opacity: 1; transition-delay: 5.3s; }
        .fm-closing-layer .credits {
          margin-top: 36px; font-size: 12px; line-height: 1.7;
          color: rgba(255,255,255,0.55);
          text-align: center; opacity: 0;
          transition: opacity 1.2s var(--ease-flow);
        }
        .fm-closing-layer .credits a {
          color: #fff; text-decoration: none;
          border-bottom: 1px solid rgba(255,255,255,0.22);
          transition: border-color 0.2s ease;
        }
        .fm-closing-layer .credits a:hover { border-bottom-color: rgba(255,255,255,0.7); }
        .fm-closing-layer.active .credits { opacity: 1; transition-delay: 7.0s; }

        .fm-fbtn {
          display: inline-flex; align-items: center; justify-content: center;
          padding: 0 36px; height: 60px; border-radius: 12px;
          font-size: 16px; font-weight: 400;
          text-decoration: none; cursor: pointer;
          transition: all 0.2s ease; min-width: 160px; border: none;
        }
        .fm-fbtn.ghost {
          color: rgba(255,255,255,0.9); background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.20);
        }
        .fm-fbtn.ghost {
          position: relative;
          overflow: hidden;
          transition: all 0.5s ease, transform 0.5s ease;
          border-width: 0.5px;
        }
        .fm-fbtn.ghost::before {
          content: '';
          position: absolute;
          top: 0;
          left: -150%;
          width: 150%;
          height: 100%;
          background: linear-gradient(
            110deg,
            transparent 40%,
            rgba(255, 255, 255, 0.03) 50%,
            transparent 60%
          );
          transition: none;
        }
        .fm-fbtn.ghost:hover::before {
          animation: shimmer 1.2s ease-out;
        }
        @keyframes shimmer {
          0% { left: -150%; }
          100% { left: 150%; }
        }
        .fm-fbtn.ghost:hover {
          background: rgba(255,255,255,0.08);
          border-color: #fff;
          color: #fff;
          transform: scale(1.03);
        }
        .fm-fbtn.primary {
          background: #15110d; color: #fff; border: 1px solid #15110d;
          box-shadow: 0 8px 32px -8px rgba(0,0,0,0.4);
          position: relative;
          overflow: hidden;
        }
        .fm-fbtn.primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -150%;
          width: 150%;
          height: 100%;
          background: linear-gradient(
            110deg,
            transparent 40%,
            rgba(255, 255, 255, 0.15) 50%,
            transparent 60%
          );
          transition: none;
        }
        .fm-fbtn.primary:hover::before {
          animation: shimmer 1.2s ease-out;
        }
        .fm-fbtn.primary:hover {
          background: #000; border-color: #000;
          transform: scale(1.03);
          box-shadow: 0 14px 40px -8px rgba(0,0,0,0.5);
        }

        .fm-timeline { position: relative; width: 100%; height: 800vh; pointer-events: none; }

        .fm-progress-dots {
          position: fixed; left: 50%; bottom: 44px;
          transform: translateX(-50%);
          display: flex; gap: 8px; z-index: 50; pointer-events: none;
        }
        .fm-progress-dots .dot {
          width: 5px; height: 5px; border-radius: 99px;
          background: rgba(255,255,255,0.22);
          transition: background 0.4s ease, transform 0.4s ease;
        }
        .fm-progress-dots .dot.on { background: rgba(255,255,255,0.9); transform: scale(1.4); }

        .fm-scroll-cue {
          position: fixed; right: 40px; bottom: 40px;
          font-size: 11px; text-transform: uppercase;
          color: rgba(255,255,255,0.55);
          display: flex; flex-direction: column; align-items: center; gap: 12px;
          z-index: 50; pointer-events: none;
          transition: opacity 0.6s ease;
        }
        .fm-scroll-cue::after {
          content: ''; width: 1px; height: 32px;
          background: linear-gradient(180deg, rgba(255,255,255,0.6), transparent);
          animation: scrollPulse 2.2s ease-in-out infinite;
        }
        @keyframes scrollPulse {
          0%, 100% { opacity: 0.4; transform: scaleY(1); transform-origin: top; }
          50%      { opacity: 1; transform: scaleY(1.3); transform-origin: top; }
        }
        .fm-scroll-cue.gone { opacity: 0; }

        @media (max-width: 920px) {
          .fm-text-layer .line { font-size: clamp(40px, 9vw, 76px); max-width: none !important; }
          .fm-text-layer.tl-hero .line { font-size: clamp(48px, 12vw, 96px); }
        }

        @media (prefers-reduced-motion: reduce) {
          .fm-img-layer { animation: none !important; }
        }
      `}</style>

      <div className="for-models-page" style={{ margin: 0, padding: 0, background: '#000', color: '#fff' }}>
        {/* Nav */}
        <NavBar
          variant="dark"
          links={[
            { label: 'Drawing Events', href: '/events' },
            { label: 'Artists', href: '/artists' },
            { label: 'Models', href: '/models', active: true },
          ]}
          primaryCta={{ label: 'Apply', onClick: handleGetStarted }}
        />

        {/* Fixed Full-Screen Stage */}
        <div className="fm-stage">
          {/* Image Layers */}
          {IMAGES.map((img, i) => (
            <div
              key={i}
              className={`fm-layer fm-img-layer ${seg.imgIdx === i ? 'active' : ''} ${i === 3 ? 'drift-reverse' : ''} ${i === 2 ? 'drift-soft' : ''}`}
              style={{
                backgroundImage: `url(${img.src})`,
                backgroundPosition: img.position,
                backgroundSize: img.size || 'cover',
                backgroundColor: img.bg || '#000',
                backgroundRepeat: 'no-repeat',
              }}
            />
          ))}

          {/* Vignette */}
          <div className="fm-vignette" style={{ opacity: seg.imgIdx >= 0 ? 1 : 0 }} />

          {/* Text Layers */}
          <div className={`fm-layer fm-text-layer tl-hero ${seg.txtIdx === 0 && textVisible && initialFadeIn ? 'active' : ''}`} style={{ transform: `translateY(${textY - 50}px)` }}>
            <h1 className="line">I want an inspired life</h1>
          </div>
          <div className={`fm-layer fm-text-layer tl-b1 ${seg.txtIdx === 1 && textVisible ? 'active' : ''}`} style={{ transform: `translateY(${textY}px)` }}>
            <p className="line">To see myself through the eyes of others...</p>
          </div>
          <div className={`fm-layer fm-text-layer tl-b2 ${seg.txtIdx === 2 && textVisible ? 'active' : ''}`} style={{ transform: `translateY(${textY}px)` }}>
            <p className="line">...in a thousand forms</p>
          </div>
          <div className={`fm-layer fm-text-layer tl-b3 ${seg.txtIdx === 3 && textVisible ? 'active' : ''}`} style={{ transform: `translateY(${textY + 150}px)` }}>
            <p className="line">To make the world beautiful</p>
          </div>
          <div className={`fm-layer fm-text-layer tl-b4 ${seg.txtIdx === 4 && textVisible ? 'active' : ''}`} style={{ transform: `translateY(${textY + 50}px)` }}>
            <p className="line">and fill it with art</p>
          </div>

          {/* Closing Section */}
          <div className={`fm-layer fm-closing-layer ${seg.txtIdx === 5 ? 'active' : ''}`}>
            <p className="copy-line">Connect with artists around the world.</p>
            <p className="copy-line">Plan your poses, and run sessions independently or with a co-host.</p>
            <p className="copy-line">You control who may join, and how recordings are shared.</p>
            <div className="buttons">
              <button onClick={handleGetStarted} className="fm-fbtn ghost">Apply</button>
              <button onClick={() => setShowLifeModelInfo(true)} className="fm-fbtn ghost">You can be a life model</button>
            </div>
            <p className="credits">
              Model: <a href="#">Olja</a><br />
              Photography: <a href="#">Simon Whittle</a><br />
              Artists: <a href="#">Raul Allen</a>, <a href="#">Jim Kerby</a>
            </p>
          </div>
        </div>

        {/* Progress Dots */}
        <div className="fm-progress-dots">
          {SEGMENTS.map((_, i) => (
            <div key={i} className={`dot ${currentIdx === i ? 'on' : ''}`} />
          ))}
        </div>

        {/* Scroll Cue */}
        <div className={`fm-scroll-cue ${!showCue ? 'gone' : ''}`}>Scroll</div>

        {/* Tall Invisible Scroll Runner */}
        <div ref={timelineRef} className="fm-timeline" />
      </div>
      <LifeModelInfoModal
        isOpen={showLifeModelInfo}
        onClose={() => setShowLifeModelInfo(false)}
      />
    </>
  );
}
